import { unzipStore } from './zipStore.js';

const QUESTION_RE = /^Question\s+(\d+)\b/i;
const OPTION_RE = /^([A-D])[.)]\s*(.*)$/;
const SKILL_SPLIT_RE = /^(.+?)\s+\|\s+(.+)$/;
const KEY_RE = /^(\d{1,3})\s+([A-D])\b\s*(.*)$/;
const SKIP_RE = /^(your answer|reasoning)\b/i;
const KNOWN_SKILLS = new Set([
  'Reading for Meaning',
  'Designing and Interpreting Experiments',
  'Using Numbers and Graphics',
]);

function decodeXml(value) {
  return String(value || '')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&apos;/g, "'");
}

function paragraphText(xml) {
  const parts = [];
  const re = /<w:t[^>]*>([^<]*)<\/w:t>|<w:tab\/?>|<w:br[^>]*\/>/g;
  let match;
  while ((match = re.exec(xml))) {
    if (match[0].startsWith('<w:tab')) parts.push(' ');
    else if (match[0].startsWith('<w:br')) parts.push('\n');
    else parts.push(decodeXml(match[1]));
  }
  return parts.join('').replace(/[ \t]+\n/g, '\n').replace(/\n{2,}/g, '\n').trim();
}

function imageIds(xml) {
  const ids = [];
  const drawings = xml.split('<w:drawing');
  for (let i = 1; i < drawings.length; i += 1) {
    const end = drawings[i].indexOf('</w:drawing>');
    const chunk = end >= 0 ? drawings[i].slice(0, end) : drawings[i];
    const embed = chunk.match(/r:embed="(rId\d+)"/);
    if (embed) ids.push(embed[1]);
  }
  return ids;
}

function splitTag(xml, tag) {
  const parts = [];
  let j = 0;
  while (j < xml.length) {
    const at = indexOfTag(xml, tag, j, false);
    if (at < 0) break;
    parts.push(at);
    j = at + tag.length + 1;
  }
  return parts.map((at, index) => {
    const next = parts[index + 1] ?? xml.length;
    return xml.slice(at, next);
  });
}

function tableRows(xml) {
  const rows = [];
  const rawRows = splitTag(xml, 'w:tr');
  for (let i = 0; i < rawRows.length; i += 1) {
    const end = rawRows[i].indexOf('</w:tr>');
    const chunk = end >= 0 ? rawRows[i].slice(0, end) : rawRows[i];
    const cells = splitTag(chunk, 'w:tc').map((cell) => {
      const cellEnd = cell.indexOf('</w:tc>');
      return paragraphText(cellEnd >= 0 ? cell.slice(0, cellEnd) : cell);
    });
    if (cells.some((cell) => cell)) rows.push(cells);
  }
  return rows;
}

function indexOfTag(xml, tag, from, closing) {
  const needle = closing ? `</${tag}` : `<${tag}`;
  let j = from;
  while (j < xml.length) {
    const at = xml.indexOf(needle, j);
    if (at < 0) return -1;
    const next = xml[at + needle.length];
    if (next === '>' || next === ' ' || next === '/' || next === '\n' || next === '\r' || next === '\t') return at;
    j = at + needle.length;
  }
  return -1;
}

function topLevelBlocks(xml) {
  const start = xml.indexOf('<w:body');
  if (start < 0) throw new Error('This DOCX has no document body.');
  let i = xml.indexOf('>', start) + 1;
  const blocks = [];

  while (i < xml.length) {
    while (xml[i] === ' ' || xml[i] === '\n' || xml[i] === '\r' || xml[i] === '\t') i += 1;
    if (xml.startsWith('</w:body>', i)) break;
    if (!xml.startsWith('<w:', i)) {
      i += 1;
      continue;
    }
    const tagMatch = xml.slice(i + 1).match(/^[\w:]+/);
    const tag = tagMatch ? tagMatch[0] : '';
    if (!tag || tag === 'w:sectPr') break;
    const gt = xml.indexOf('>', i);
    if (gt < 0) break;
    if (xml[gt - 1] === '/') {
      i = gt + 1;
      continue;
    }
    let depth = 1;
    let j = gt + 1;
    while (depth > 0 && j < xml.length) {
      const nextOpen = indexOfTag(xml, tag, j, false);
      const nextClose = indexOfTag(xml, tag, j, true);
      if (nextClose < 0) break;
      if (nextOpen >= 0 && nextOpen < nextClose) {
        depth += 1;
        j = xml.indexOf('>', nextOpen) + 1;
      } else {
        depth -= 1;
        const closeEnd = xml.indexOf('>', nextClose);
        j = closeEnd < 0 ? nextClose + tag.length + 3 : closeEnd + 1;
      }
    }
    if (tag === 'w:p' || tag === 'w:tbl') blocks.push({ tag, xml: xml.slice(i, j) });
    i = j;
  }
  return blocks;
}

function blankQuestion(sourceNumber) {
  return {
    sourceNumber,
    title: '',
    skill: '',
    domain: '',
    blocks: [],
    stem: '',
    options: [],
    correct: 0,
    explanation: '',
  };
}

function ensureStem(question) {
  if (question.stem) return;
  for (let i = question.blocks.length - 1; i >= 0; i -= 1) {
    if (question.blocks[i].type === 'text') {
      question.stem = question.blocks[i].text;
      question.blocks.splice(i, 1);
      return;
    }
  }
}

function pushImages(question, ids, optionIndex) {
  ids.forEach((id) => {
    if (optionIndex >= 0) {
      const option = question.options[optionIndex];
      if (!option.mediaIds.includes(id)) option.mediaIds.push(id);
      return;
    }
    const last = question.blocks[question.blocks.length - 1];
    if (last?.type === 'image' && last.mediaId === id) return;
    question.blocks.push({ type: 'image', mediaId: id });
  });
}

function absorbQuestionParagraph(question, text, ids) {
  const option = text.match(OPTION_RE);
  if (option && (question.options.length > 0 || option[1] === 'A')) {
    ensureStem(question);
    const index = option[1].charCodeAt(0) - 65;
    while (question.options.length < index) {
      question.options.push({ text: '', mediaIds: [] });
    }
    if (question.options.length === index) {
      question.options.push({ text: option[2].trim(), mediaIds: [] });
    } else {
      question.options[index].text = option[2].trim();
    }
    pushImages(question, ids, index);
    return;
  }
  if (SKIP_RE.test(text)) return;
  if (!question.title && text) {
    question.title = text;
    pushImages(question, ids, -1);
    return;
  }
  const skill = text.match(SKILL_SPLIT_RE);
  if (!question.skill && skill) {
    question.skill = skill[1].trim();
    question.domain = skill[2].trim();
    return;
  }
  if (text) question.blocks.push({ type: 'text', text });
  pushImages(question, ids, question.options.length ? question.options.length - 1 : -1);
}

function looksLikeKeyTable(rows) {
  const cells = rows.flat().map((cell) => cell.trim()).filter(Boolean);
  if (!cells.length) return false;
  return cells.filter((cell) => KEY_RE.test(cell)).length >= Math.ceil(cells.length / 2);
}

function parseRelationships(xml) {
  const map = {};
  const re = /Id="(rId\d+)"[^>]*Target="([^"]+)"/g;
  let match;
  while ((match = re.exec(xml))) {
    const target = decodeURIComponent(match[2]).replace(/^\//, '');
    map[match[1]] = target.startsWith('word/') ? target : `word/${target.replace(/^\.\.\//, '')}`;
  }
  return map;
}

function contentTypeFor(name) {
  const ext = String(name).split('.').pop().toLowerCase();
  if (ext === 'jpg' || ext === 'jpeg') return 'image/jpeg';
  if (ext === 'gif') return 'image/gif';
  if (ext === 'webp') return 'image/webp';
  if (ext === 'bmp') return 'image/bmp';
  return 'image/png';
}

function finalizeQuestions(questions, answers, explanations) {
  return questions
    .filter((question) => question.options.length >= 2 && question.stem)
    .map((question) => {
      while (question.options.length < 4) question.options.push({ text: '', mediaIds: [] });
      const letter = answers.get(question.sourceNumber);
      const correct = letter ? letter.charCodeAt(0) - 65 : 0;
      return {
        hasAnswerKey: Boolean(letter),
        sourceNumber: question.sourceNumber,
        title: question.title,
        skill: question.skill,
        domain: question.domain,
        stem: question.stem,
        explanation: explanations.get(question.sourceNumber) || '',
        exhibit: question.blocks,
        options: question.options.slice(0, 4).map((option) => ({
          text: option.text,
          mediaIds: option.mediaIds || [],
        })),
        correct: correct >= 0 && correct <= 3 ? correct : 0,
      };
    });
}

export function parsePracticeExamDocumentXml(documentXml, relsXml = '') {
  const blocks = topLevelBlocks(documentXml);
  const questions = [];
  const answers = new Map();
  const explanations = new Map();
  let current = null;
  let mode = 'questions';
  let pending = null;

  function finishQuestion() {
    if (!current) return;
    ensureStem(current);
    questions.push(current);
    current = null;
  }

  function flushExplanation() {
    if (!pending) return;
    explanations.set(pending.number, pending.text.trim());
    pending = null;
  }

  function takeKey(text) {
    const key = String(text || '').trim().match(KEY_RE);
    if (!key) return false;
    if (!key[3]) {
      answers.set(Number(key[1]), key[2].toUpperCase());
      return true;
    }
    return false;
  }

  blocks.forEach((block) => {
    if (block.tag === 'w:tbl') {
      const rows = tableRows(block.xml);
      const ids = imageIds(block.xml);
      if (mode === 'key' || looksLikeKeyTable(rows)) {
        mode = 'key';
        finishQuestion();
        rows.flat().forEach((cell) => takeKey(cell));
        return;
      }
      if (current) {
        if (rows.length) current.blocks.push({ type: 'table', rows });
        pushImages(current, ids, current.options.length ? current.options.length - 1 : -1);
      }
      return;
    }

    const text = paragraphText(block.xml);
    const ids = imageIds(block.xml);
    if (!text && !ids.length) return;

    if (/^quick answer key/i.test(text)) {
      finishQuestion();
      mode = 'key';
      return;
    }
    if (/^answers and explanations/i.test(text)) {
      if (questions.length > 0 || mode === 'key') {
        finishQuestion();
        flushExplanation();
        mode = 'explain';
      }
      return;
    }

    const heading = text.match(QUESTION_RE);
    if (heading && mode === 'questions') {
      finishQuestion();
      current = blankQuestion(Number(heading[1]));
      return;
    }

    if (mode === 'key') {
      takeKey(text);
      return;
    }

    if (mode === 'explain') {
      const explained = text.match(KEY_RE);
      if (explained && explained[3]) {
        flushExplanation();
        pending = { number: Number(explained[1]), text: '' };
        answers.set(pending.number, explained[2].toUpperCase());
        return;
      }
      if (KNOWN_SKILLS.has(text)) {
        flushExplanation();
        return;
      }
      if (pending && text) {
        pending.text = pending.text ? `${pending.text}\n${text}` : text;
      }
      return;
    }

    if (current) absorbQuestionParagraph(current, text, ids);
  });

  finishQuestion();
  flushExplanation();

  const mediaIds = new Set();
  const parsed = finalizeQuestions(questions, answers, explanations);
  parsed.forEach((question) => {
    question.exhibit.forEach((block) => {
      if (block.type === 'image') mediaIds.add(block.mediaId);
    });
    question.options.forEach((option) => option.mediaIds.forEach((id) => mediaIds.add(id)));
  });

  return {
    questions: parsed,
    relationships: parseRelationships(relsXml),
    mediaIds: [...mediaIds],
  };
}

export async function parsePracticeExamDocx(buffer) {
  const files = await unzipStore(buffer);
  const documentBytes = files['word/document.xml'];
  if (!documentBytes) throw new Error('This file is not a Word document.');
  const documentXml = new TextDecoder('utf-8').decode(documentBytes);
  const relsBytes = files['word/_rels/document.xml.rels'];
  const relsXml = relsBytes ? new TextDecoder('utf-8').decode(relsBytes) : '';
  const parsed = parsePracticeExamDocumentXml(documentXml, relsXml);
  const missingKey = parsed.questions.filter((question) => !question.hasAnswerKey);
  if (missingKey.length) {
    const sample = missingKey.slice(0, 5).map((question) => question.sourceNumber).join(', ');
    throw new Error(`${missingKey.length} questions have no answer in the document’s answer key (${sample}).`);
  }

  const media = {};
  parsed.mediaIds.forEach((id) => {
    const target = parsed.relationships[id];
    const bytes = target ? files[target] : null;
    if (!bytes) return;
    const name = target.split('/').pop() || `${id}.png`;
    media[id] = {
      bytes,
      name,
      contentType: contentTypeFor(name),
    };
  });

  return {
    questions: parsed.questions,
    media,
  };
}
